/**
 * PUBLIC certificate verification page (guest-first, no account needed).
 *
 * Certificate id is the credential (unguessable UUID emitted by
 * server): this view verifies it against public REST
 * `/api/certificates/:id/verify` and displays sealed snapshot if authentic,
 * or an "invalid" state without leaking anything else.
 */
import { type Component, createMemo, Match, Switch } from "solid-js";
import { createSafeQuery } from "@/lib/api";
import { msg } from "@lingui/core/macro";
import { BadgeCheck, ShieldX } from "@fleet/icons";
import {
  buildCertificateContent,
  type CertificateVerificationDTO,
  courseQueryKeys,
  fetchCertificateVerification,
} from "@aula/shared";
import { useI18n } from "@/providers/i18n-provider";
import { SERVER_URL } from "@/lib/runtime-config";
import { GlassSurface, Skeleton } from "@fleet/ui";
import { AppBackground } from "@/components/app-background";
import { CertificateCard } from "./certificate-card";

export const VerifyCertificateView: Component<{ certId: string }> = (props) => {
  const { t, locale } = useI18n();

  const verification = createSafeQuery<CertificateVerificationDTO>(() => ({
    queryKey: courseQueryKeys.certificateVerification(props.certId),
    queryFn: ({ signal }) => fetchCertificateVerification(SERVER_URL, props.certId, { signal }),
    staleTime: 30_000,
    retry: 1,
  }));

  const content = createMemo(() => {
    const cert = verification.data?.certificate;
    if (!cert) {
      return null;
    }
    // The REST DTO arrives in camelCase; template consumes snake_case shape
    // of synced snapshot — normalized here.
    return buildCertificateContent(
      {
        id: cert.id,
        learner_name: cert.learnerName,
        course_title: cert.courseTitle,
        course_version: cert.courseVersion,
        org_name: cert.orgName,
        issued_at: cert.issuedAt,
      },
      { verifyBaseUrl: globalThis.location.origin, locale: locale() },
    );
  });

  return (
    <div class="relative min-h-dvh">
      <AppBackground />
      <div class="mx-auto max-w-3xl space-y-6 p-4 py-10 md:p-8">
        <Switch>
          <Match when={verification.isLoading}>
            <Skeleton class="h-96 w-full rounded-3xl" />
          </Match>
          <Match when={verification.isError}>
            <GlassSurface class="p-8 text-center" data-testid="certificate-verify-error">
              <p class="text-muted-foreground">
                {t(msg`Couldn't reach the verification service. Try again later.`)}
              </p>
            </GlassSurface>
          </Match>
          <Match when={verification.data?.valid && content()}>
            {(c) => (
              <>
                <div
                  class="flex items-center justify-center gap-2 rounded-2xl bg-success/15 px-4 py-3 font-semibold text-success"
                  data-testid="certificate-verify-valid"
                >
                  <BadgeCheck class="h-5 w-5" />
                  {t(msg`This certificate is authentic`)}
                </div>
                <CertificateCard content={c()} />
              </>
            )}
          </Match>
          <Match when={verification.data && !verification.data.valid}>
            <div
              class="flex flex-col items-center gap-3 rounded-3xl border-2 border-dashed border-danger/40 p-12 text-center"
              data-testid="certificate-verify-invalid"
            >
              <ShieldX class="h-10 w-10 text-danger" />
              <h1 class="font-display text-xl font-bold">{t(msg`Certificate not valid`)}</h1>
              <p class="text-sm text-muted-foreground">
                {t(
                  msg`No authentic certificate matches this ID. It may have been revoked or the link is wrong.`,
                )}
              </p>
            </div>
          </Match>
        </Switch>
      </div>
    </div>
  );
};
